import React from 'react';
import { Card } from '@/components/ui/card';
import { Building2, Phone, Mail, MapPin, ChevronRight, AlertTriangle } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import StarRating from './StarRating';
import { cn } from '@/lib/utils';
import { normalizeLeadStatus, isCustomerStatus, isLostStatus } from '@/lib/leadStatus';

const borderColors = {
  'Potenzieller Interessent': 'border-l-purple-500',
  'Interessenten': 'border-l-amber-500',
  'Kunde': 'border-l-emerald-500',
  'Verlorene Kunden': 'border-l-red-500',
  'eBay': 'border-l-yellow-500',
  'Shopify': 'border-l-green-500',
};

export default function LeadCard({ lead, onClick, compact = false }) {
  const fullAddress = [lead.strasse, lead.plz, lead.ort].filter(Boolean).join(', ');

  return (
    <Card
      className={cn(
        'border-l-4 hover:shadow-md transition-all cursor-pointer bg-white',
        borderColors[normalizeLeadStatus(lead.status)] || 'border-l-slate-300'
      )}
      onClick={() => onClick?.(lead)}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <h3 className="font-semibold text-slate-900 truncate">{lead.name}</h3>
              {!lead.email && (
                <span title="E-Mail fehlt — bitte pflegen!">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                </span>
              )}
            </div>

            {!compact && (
              <>
                <p className="text-sm text-slate-500 mb-2">{lead.branche}</p>

                {(isCustomerStatus(lead.status) || isLostStatus(lead.status)) && lead.rating > 0 && (
                  <div className="mb-2">
                    <StarRating rating={lead.rating} readonly size="sm" />
                  </div>
                )}

                {fullAddress && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                    <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <span className="truncate">{fullAddress}</span>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 mt-2">
                  {lead.telefon && (
                    <a
                      href={`tel:${lead.telefon}`}
                      className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Phone className="w-3.5 h-3.5" />
                      <span>{lead.telefon}</span>
                    </a>
                  )}
                  {lead.email && (
                    <a
                      href={`mailto:${lead.email}`}
                      className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Mail className="w-3.5 h-3.5" />
                      <span className="truncate max-w-[150px]">{lead.email}</span>
                    </a>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
            <StatusBadge status={lead.status} type="lead" />
            <ChevronRight className="w-5 h-5 text-slate-300" />
          </div>
        </div>
      </div>
    </Card>
  );
}
